/**
 * The player's profile, persisted in localStorage through progression's versioned
 * `serialize` / `deserialize` (which own the save migrations, v0 → v1 → v2 …).
 *
 *   const store = new ProfileStore();               // loads (or creates) the profile
 *   store.profile                                   // current immutable snapshot
 *   store.subscribe((p, prev) => render(p));        // change notifications
 *   store.set(next)                                 // replace; saves debounced
 *   store.apply(equipItem(store.profile, c, id))    // OpResult helper → error code or null
 *   store.flush()                                   // write pending changes now (pagehide)
 *   await store.act('equip', { classId, itemId })   // an economy intent (meta/intents.ts)
 *
 * Online (docs/design/multiplayer.md §4.2): after `goOnline(remote, …)` the profile is the
 * server's. Intents go to the server; deterministic ones are applied locally at once (the
 * server's answer then replaces them), rolls wait for the server. Nothing is written to the
 * local save while online: it stays the offline profile.
 *
 * Profiles are treated as immutable: progression functions return new objects, and `set`
 * only notifies when the reference changes. Every storage access is wrapped in try/catch
 * (private mode, quota, disabled storage): the game keeps running in memory and reports
 * the problem through `lastError` / `onError`.
 */
import type { ClassId } from '@keystone/sim';
import type { OpError, OpResult, Profile } from '@keystone/progression';
import { PROFILE_CLASSES, PROFILE_VERSION, SaveError, deserialize, newProfile, serialize, validateProfile } from '@keystone/progression';
import { type IntentArgs, type IntentName, type IntentResult, INTENTS, runIntent } from './intents';
import { localDateString } from './forge/localDate';

export const PROFILE_STORAGE_KEY = 'keystone.profile';
/** A save that failed to load is copied here before being replaced (never silently lost). */
export const CORRUPT_SUFFIX = '.corrupt';
export const DEFAULT_SAVE_DEBOUNCE_MS = 400;

/** The subset of the Web Storage API the store needs (tests pass an in-memory one). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type LoadStatus =
  /** No save found: a new profile was created. */
  | 'fresh'
  /** Save loaded at the current version. */
  | 'loaded'
  /** Save loaded and migrated from an older version (re-saved immediately). */
  | 'migrated'
  /** Save existed but could not be read; it was backed up under `${key}.corrupt` and a fresh profile started. */
  | 'recovered'
  /** Storage is unavailable; the profile lives in memory only. */
  | 'memory';

export type ProfileListener = (profile: Profile, prev: Profile) => void;

export interface ProfileStoreOptions {
  /** Storage backend. Default: window.localStorage when reachable, else memory only. Pass null for memory only. */
  storage?: StorageLike | null;
  key?: string;
  debounceMs?: number;
  /** Class for a brand-new profile (default: the first playable class). */
  defaultMainClass?: ClassId;
  /** Seed for a brand-new profile (default: time-based; tests pass a fixed one). */
  newSeed?: () => number;
  onError?: (err: unknown, during: 'load' | 'save') => void;
}

/** Where intents go while online (meta/api.ts ApiClient implements it). */
export interface ProfileRemote {
  act(op: string, args: unknown, base: number, localDate: string): Promise<{ status: number; profile?: string | null; version?: number; result?: Record<string, unknown>; reason?: string; error?: string }>;
}

export type ImportResult = { ok: true; profile: Profile; migratedFrom: number | null } | { ok: false; error: string };

function defaultStorage(): StorageLike | null {
  try {
    const s = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (!s) return null;
    const probe = '__keystone_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

/** Version field of a raw save without fully parsing it (null when unreadable). */
export function rawSaveVersion(json: string): number | null {
  try {
    const o = JSON.parse(json) as { version?: unknown } | null;
    if (!o || typeof o !== 'object') return null;
    return typeof o.version === 'number' ? o.version : 0;
  } catch {
    return null;
  }
}

/**
 * Parse + migrate + validate a save string. Wraps progression's `deserialize` and also
 * rejects profiles that fail `validateProfile` (e.g. a hand-edited export).
 */
export function parseProfile(json: string): ImportResult {
  try {
    const from = rawSaveVersion(json);
    const p = deserialize(json);
    const errs = validateProfile(p);
    if (errs.length) return { ok: false, error: `profile is inconsistent: ${errs.slice(0, 3).join('; ')}` };
    // v5: no character until character creation; a created one must have its progress (validateProfile).
    if (p.character && !p.characters[p.character.classId]) return { ok: false, error: `profile has no progress for its character's class ${p.character.classId}` };
    return { ok: true, profile: p, migratedFrom: from !== null && from < PROFILE_VERSION ? from : null };
  } catch (err) {
    return { ok: false, error: err instanceof SaveError ? err.message : `could not read profile: ${String(err)}` };
  }
}

export class ProfileStore {
  readonly key: string;
  readonly debounceMs: number;
  readonly status: LoadStatus;
  /** Problem found while loading (kept for the session so the hub can show it), or null. */
  loadError: string | null = null;
  /** Last failed save, cleared by the next successful one. */
  saveError: string | null = null;

  private current: Profile;
  private readonly storage: StorageLike | null;
  private readonly listeners = new Set<ProfileListener>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private dirty = false;
  private readonly opts: ProfileStoreOptions;
  private remote: ProfileRemote | null = null;
  /** Server profile version the next intent is based on (online only). */
  version = 0;
  /** Online intents run one after another (each needs the previous version). */
  private queue: Promise<unknown> = Promise.resolve();
  /** Server problems while online (rejected intent, conflict, unreachable). */
  onRemoteError: ((message: string) => void) | null = null;
  /** Called when the store drops back to local mode (the API became unreachable). */
  onOffline: ((reason: string) => void) | null = null;

  constructor(opts: ProfileStoreOptions = {}) {
    this.opts = opts;
    this.key = opts.key ?? PROFILE_STORAGE_KEY;
    this.debounceMs = opts.debounceMs ?? DEFAULT_SAVE_DEBOUNCE_MS;
    this.storage = opts.storage === undefined ? defaultStorage() : opts.storage;
    const { profile, status } = this.load();
    this.current = profile;
    this.status = status;
    if (status === 'migrated' || status === 'fresh' || status === 'recovered') this.save();
  }

  /** The problem to show the player (save problems first), or null. */
  get lastError(): string | null {
    return this.saveError ?? this.loadError;
  }

  get profile(): Profile {
    return this.current;
  }

  /** True while a debounced save is pending. */
  get pending(): boolean {
    return this.dirty;
  }

  get persistent(): boolean {
    return this.remote !== null || this.storage !== null;
  }

  /** True while the profile is the server's (docs/design/multiplayer.md). */
  get online(): boolean {
    return this.remote !== null;
  }

  /** Switch to the server's profile. Returns false (and stays local) when it can't be read. */
  goOnline(remote: ProfileRemote, json: string, version: number): boolean {
    const r = parseProfile(json);
    if (!r.ok) return false;
    this.flush();
    this.remote = remote;
    this.version = version;
    this.set(r.profile);
    return true;
  }

  /** Back to the local save (the API became unreachable). The server copy is not kept locally. */
  goOffline(reason: string): void {
    if (!this.remote) return;
    this.remote = null;
    this.version = 0;
    const { profile } = this.load();
    this.set(profile);
    this.onOffline?.(reason);
  }

  /** Replace the profile with a server answer (online only). */
  acceptServer(json: string | null | undefined, version: number | undefined): void {
    if (!this.remote || !json || typeof version !== 'number') return;
    const r = parseProfile(json);
    if (!r.ok) {
      this.onRemoteError?.(`The server sent an unreadable profile (${r.error}).`);
      return;
    }
    this.version = version;
    // The usual case: the server agrees with the local prediction; keep the reference (no re-render).
    if (serialize(r.profile) === serialize(this.current)) return;
    this.set(r.profile);
  }

  /**
   * Run an economy intent (meta/intents.ts). Offline it runs here and resolves at once. Online a
   * deterministic intent is applied locally at once and confirmed by the server in the
   * background; a roll resolves with the server's outcome. Resolves with the outcome either way.
   */
  act<K extends IntentName>(name: K, args: IntentArgs<K>): Promise<IntentResult<K>> {
    const localDate = localDateString();
    // Offline the browser clock drives the field kill budget and dungeon timers; online the server ignores this and uses its own.
    const env = { localDate, nowMs: Date.now(), newSeed: () => this.newSeed() };
    if (!this.remote) {
      const r = runIntent(name, this.current, args, env);
      if (r.ok) this.set(r.profile);
      return Promise.resolve(r);
    }
    const remote = this.remote;
    if (!INTENTS[name].rolls) {
      const predicted = runIntent(name, this.current, args, env);
      if (!predicted.ok) return Promise.resolve(predicted);
      this.set(predicted.profile);
      void this.send(remote, name, args, localDate);
      return Promise.resolve(predicted);
    }
    return this.send(remote, name, args, localDate);
  }

  /** Queue one intent for the server; resolves with its outcome (the server's profile applied). */
  private send<K extends IntentName>(remote: ProfileRemote, name: K, args: IntentArgs<K>, localDate: string): Promise<IntentResult<K>> {
    const run = async (): Promise<IntentResult<K>> => {
      let r: Awaited<ReturnType<ProfileRemote['act']>>;
      try {
        r = await remote.act(name, args, this.version, localDate);
      } catch (err) {
        this.goOffline(err instanceof Error ? err.message : String(err));
        return { ok: false, reason: 'The server is unreachable; playing offline.' } as IntentResult<K>;
      }
      if (this.remote !== remote) return { ok: false, reason: 'offline' } as IntentResult<K>;
      this.acceptServer(r.profile, r.version);
      if (r.status === 200) return { ...(r.result ?? {}), ok: true, profile: this.current } as unknown as IntentResult<K>;
      const reason = r.status === 409 ? 'Your profile changed elsewhere; showing the latest.' : String(r.reason ?? r.error ?? `HTTP ${r.status}`);
      if (r.status !== 422) this.onRemoteError?.(reason);
      return { ok: false, reason } as IntentResult<K>;
    };
    const p = this.queue.then(run, run);
    this.queue = p.catch(() => undefined);
    return p;
  }

  subscribe(fn: ProfileListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Replace the profile, notify subscribers and schedule a save (`immediate` writes now). */
  set(next: Profile, opts: { immediate?: boolean } = {}): void {
    if (next === this.current) return;
    const prev = this.current;
    this.current = next;
    this.dirty = true;
    if (opts.immediate) this.save();
    else this.schedule();
    for (const fn of [...this.listeners]) {
      try {
        fn(next, prev);
      } catch (err) {
        console.error('[profile] listener threw', err);
      }
    }
  }

  /** `set(fn(profile))`. */
  update(fn: (p: Profile) => Profile, opts: { immediate?: boolean } = {}): void {
    this.set(fn(this.current), opts);
  }

  /** Commit a progression OpResult. Returns null on success, else the error code (nothing changes). */
  apply(result: OpResult, opts: { immediate?: boolean } = {}): OpError | null {
    if (!result.ok) return result.reason;
    this.set(result.profile, opts);
    return null;
  }

  /** Write now. Returns false when storage is unavailable or the write failed. */
  save(): boolean {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.dirty = false;
    // Online the server owns the profile; the local save stays the offline profile.
    if (this.remote) return true;
    if (!this.storage) return false;
    try {
      this.storage.setItem(this.key, serialize(this.current));
      this.saveError = null;
      return true;
    } catch (err) {
      this.saveError = `Could not save your profile (${err instanceof Error ? err.message : String(err)}).`;
      this.opts.onError?.(err, 'save');
      return false;
    }
  }

  /** Write a pending debounced save immediately (call on pagehide / before a run). */
  flush(): void {
    if (this.dirty) this.save();
  }

  /** Start over with a new profile (the old save is overwritten immediately). */
  reset(mainClassId: ClassId = this.current.mainClassId): Profile {
    if (this.remote) {
      void this.act('reset', { mainClassId });
      return this.current;
    }
    const p = newProfile(mainClassId, this.newSeed());
    this.set(p, { immediate: true });
    return p;
  }

  /** The current profile as a JSON string (export / clipboard). */
  exportJson(): string {
    return serialize(this.current);
  }

  /** Replace the profile from a JSON export (migrated + validated). Saves immediately on success. */
  importJson(json: string): ImportResult {
    if (this.remote) return { ok: false, error: 'Importing replaces a local profile only; your online profile is kept on the server.' };
    const r = parseProfile(json.trim());
    if (r.ok) this.set(r.profile, { immediate: true });
    return r;
  }

  dispose(): void {
    this.flush();
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.listeners.clear();
  }

  private schedule(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.save();
    }, this.debounceMs);
  }

  /** Fresh seed for a new profile (UI-side entropy). */
  newSeed(): number {
    if (this.opts.newSeed) return this.opts.newSeed() >>> 0;
    // UI-side entropy for a new profile only (the sim/progression stay deterministic per seed).
    return (Date.now() ^ Math.floor(Math.random() * 0x7fffffff)) >>> 0;
  }

  private fresh(): Profile {
    const main = this.opts.defaultMainClass ?? PROFILE_CLASSES[0] ?? 'warden';
    return newProfile(main, this.newSeed());
  }

  private load(): { profile: Profile; status: LoadStatus } {
    if (!this.storage) return { profile: this.fresh(), status: 'memory' };
    let raw: string | null;
    try {
      raw = this.storage.getItem(this.key);
    } catch (err) {
      this.loadError = 'Saved profile could not be read; playing without saving.';
      this.opts.onError?.(err, 'load');
      return { profile: this.fresh(), status: 'memory' };
    }
    if (raw === null || raw === '') return { profile: this.fresh(), status: 'fresh' };
    const r = parseProfile(raw);
    if (r.ok) return { profile: r.profile, status: r.migratedFrom !== null ? 'migrated' : 'loaded' };
    try {
      this.storage.setItem(this.key + CORRUPT_SUFFIX, raw);
    } catch {
      /* best effort */
    }
    this.loadError = `Your saved profile could not be loaded (${r.error}). It was backed up and a new profile was started.`;
    this.opts.onError?.(new Error(r.error), 'load');
    return { profile: this.fresh(), status: 'recovered' };
  }
}

/** In-memory StorageLike (tests, and a fallback for callers that want explicit memory mode). */
export function memoryStorage(init: Record<string, string> = {}): StorageLike & { data: Map<string, string> } {
  const data = new Map(Object.entries(init));
  return {
    data,
    getItem: (k) => data.get(k) ?? null,
    setItem: (k, v) => void data.set(k, String(v)),
    removeItem: (k) => void data.delete(k),
  };
}
